import React, {useContext, useEffect, useRef, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Autocomplete,
  Avatar, Checkbox,
  Chip,
  Fab,
  FormControlLabel,
  FormGroup,
  OutlinedInput,
  Tooltip,
  Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AttachmentIcon from "@mui/icons-material/Attachment";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import CloseIcon from '@mui/icons-material/Close';
import {getOrganisations, searchInvoiceNumbers, searchOutputActivities} from "../../utils/api";
import {DescriptionList, DescriptionTerm, Description} from "../parts/Description.parts";
import AddIcon from "@mui/icons-material/Add";
import BasicAutocomplete from "../autocompletes/BasicAutocomplete";
import Box from "@mui/material/Box";
import {FileDrop} from "react-file-drop";
import InvoiceNumberAutocomplete from "../autocompletes/InvoiceNumberAutocomplete";
import {UserContext} from "../auth/UserProvider";
import * as Yup from "yup";
import {useFormik} from "formik";

export default function AddReceiptDialog(props) {
  const { classes, entity, confirmationHandler, action = 'create' } = props;
  const [open, setOpen] = useState(false);
  const [payee, setPayee] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [budgetGroup, setBudgetGroup] = useState('');
  const [budget, setBudget] = useState('');
  const [outputActivity, setOutputActivity] = useState(null);
  const [currency, setCurrency] = useState({ id: 2, code: 'USD', name: 'US Dollar' });
  const [intermediateCurrency, setIntermediateCurrency] =
      useState({ id: 2, code: 'USD', name: 'US Dollar' });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState('');
  const [intermediateExchangeRate, setIntermediateExchangeRate] = useState('');
  const [transactionRefPartner, setTransactionRefPartner] = useState('');
  const [organisation, setOrganisation] = React.useState(undefined);
  const [availableOrganisations, setAvailableOrganisations] = React.useState([]);
  const [attachments, setAttachments] = useState([]);
  const [hideIntermediateFields, setHideIntermediateFields] = useState(true);

  const authState = useContext(UserContext);

  const cancelRequest = useRef();

  const fileInputRef = useRef(null);

  const ReceiptSchema = Yup.object().shape({
    payee: Yup.string().required('This field is required'),
    date: Yup.date().required('Transaction date is required'),
    description: Yup.string().required('This field is required'),
    amount: Yup.number().required('This field is required'),
    invoiceNumber: Yup.string().required('This field is required'),
    exchangeRate: Yup.number().required('This field is required'),
    intermediateExchangeRate: Yup.number(),
    transactionRefPartner: Yup.string().required('This field is required'),
    outputActivity: Yup.object().nullable(),
    currency: Yup.object().required('This field is required'),
    intermediateCurrency: Yup.object(),
    budgetGroup: Yup.string().required('This field is required'),
    budget: Yup.string().required('This field is required'),
  });

  const receiptForm = useFormik({
    initialValues: {
      payee: '',
      date: '',
      description: '',
      invoiceNumber: '',
      amount: '',
      exchangeRate: '',
      intermediateExchangeRate: '',
      transactionRefPartner: '',
      outputActivity: null,
      currency: { id: 2, code: 'USD', name: 'US Dollar' },
      intermediateCurrency: { id: 2, code: 'USD', name: 'US Dollar' },
      budgetGroup: '',
      budget: '',
      organisation: undefined,
    },
    validationSchema: ReceiptSchema,
  });

  const currencyOptions = [
    { id: 1, code: 'EUR', name: 'Euro' },
    { id: 2, code: 'USD', name: 'US Dollar' },
    { id: 3, code: 'VND', name: 'Vietnamese Dong' },
    { id: 4, code: 'PHP', name: 'Philippine Peso' },
    { id: 5, code: 'IDR', name: 'Indonesian Rupiah' },
    { id: 6, code: 'KHR', name: 'Cambodian Riel' },
  ];

  const budgetGroupOptions = [
    { id: 1, code: '1.1.1', name: '1.1.1 Technical', orgs: ['BfdW', 'ICSC'] },
    { id: 2, code: '1.1.2', name: '1.1.2 Administrative/ support staff', orgs: ['BfdW', 'ICSC'] },
    { id: 3, code: '1.2', name: '1.2 Salaries (gross salaries including social security charges and other related costs, expat/int. staff)', orgs: ['BfdW'] },
    { id: 4, code: '1.3', name: '1.3 Per diems for missions/travel', orgs: ['ALL'] },
    { id: 5, code: '2.1', name: '2.1 International travel (staff assigned to the Action)', orgs: ['ALL'] },
    { id: 6, code: '2.2', name: '2.2 National travel', orgs: ['ALL'] },
    { id: 7, code: '3', name: '3. Equipment and supplies', orgs: ['BfdW', 'ICSC'] },
    { id: 8, code: '4', name: '4. Local office', orgs: ['BfdW', 'ICSC'] },
    //{ id: 9, code: '5.1', name: '5.1 Publications', orgs: ['BfdW'] },
    { id: 10, code: '5.2', name: '5.2 Studies, research', orgs: ['BfdW'] },
    { id: 11, code: '5.3', name: '5.3 Expenditure verification', orgs: ['BfdW'] },
    { id: 12, code: '5.4', name: '5.4 Evaluation Costs', orgs: ['BfdW'] },
    //{ id: 13, code: '5.5', name: '5.5 Translation, interpreters', orgs: ['BfdW'] },
    { id: 14, code: '5.6', name: '5.6 Financial services (bank guarantee costs etc.)', orgs: ['BfdW'] },
    { id: 15, code: '5.7', name: '5.7 Cost of Conferences/seminars', orgs: ['ALL'] },
    { id: 16, code: '5.8', name: '5.8 Visibility actions', orgs: ['ALL'] },
    { id: 17, code: '6', name: '6. Other', orgs: ['ALL'] },
  ];

  const budgetOptions = [
    { id: 1, code: '1.1.1.1', name: '1.1.1.1 Project Director AFV 14%', group: '1.1.1', orgs: ['BfdW'] },
    { id: 2, code: '1.1.1.2', name: '1.1.1.2 Programme Coordination Manager AFV 28%', group: '1.1.1', orgs: ['BfdW'] },
    { id: 3, code: '1.1.1.3', name: '1.1.1.3 Project Operations Coordinator AFV 17%', group: '1.1.1', orgs: ['BfdW'] },
    { id: 4, code: '1.1.1.4', name: '1.1.1.4 Project Officer AFV 33%', group: '1.1.1', orgs: ['BfdW'] },
    { id: 5, code: '1.1.1.5', name: '1.1.1.5 MEAL Officer - AFV', group: '1.1.1', orgs: ['BfdW'] },
    { id: 6, code: '1.1.1.6', name: '1.1.1.6 Communication & Visibility Officer AFV 36%', group: '1.1.1', orgs: ['BfdW'] },
    { id: 7, code: '1.1.1.7', name: '1.1.1.7 Project manager & oversight ICSC 30%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 8, code: '1.1.1.8', name: '1.1.1.8 Project Coordinator (Overall) ICSC 80%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 9, code: '1.1.1.9', name: '1.1.1.9 Climate Policy Officer ICSC 60%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 10, code: '1.1.1.11', name: '1.1.1.11 Comms officer content ICSC 50%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 11, code: '1.1.1.12', name: '1.1.1.12 Comms officer digital ICSC 50%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 12, code: '1.1.1.13', name: '1.1.1.13 MEAL officer ICSC 50%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 13, code: '1.1.1.14', name: '1.1.1.14 Project Coordinator (Communication) ICSC 50%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 14, code: '1.1.1.15', name: '1.1.1.15 Senior Climate Policy Officer ICSC 60%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 15, code: '1.1.1.16', name: '1.1.1.16 Senior Climate Policy Officer ICSC 60%', group: '1.1.1', orgs: ['BfdW', 'ICSC'] },
    { id: 16, code: '1.1.2.1', name: '1.1.2.1 Finance Coordinator - AFV 31%', group: '1.1.2', orgs: ['BfdW'] },
    { id: 17, code: '1.1.2.2', name: '1.1.2.2 Finance Officer AFV 23%', group: '1.1.2', orgs: ['BfdW'] },
    { id: 18, code: '1.1.2.3', name: '1.1.2.3 Project Finance Support Officer AFV 50%', group: '1.1.2', orgs: ['BfdW'] },
    { id: 19, code: '1.1.2.4', name: '1.1.2.4 Admin and Logistics Officer AFV 39%', group: '1.1.2', orgs: ['BfdW'] },
    { id: 20, code: '1.1.2.5', name: '1.1.2.5 Project Assistant BFDW 100%', group: '1.1.2', orgs: ['BfdW'] },
    { id: 21, code: '1.1.2.6', name: '1.1.2.6 Admin and Logistics Officer ICSC 50%', group: '1.1.2', orgs: ['BfdW', 'ICSC'] },
    { id: 22, code: '1.1.2.7', name: '1.1.2.7 Finance officer ICSC 80%', group: '1.1.2', orgs: ['BfdW', 'ICSC'] },
    { id: 23, code: '1.1.2.8', name: '1.1.2.8 Project assistant ICSC 50%', group: '1.1.2', orgs: ['BfdW', 'ICSC'] },
    { id: 24, code: '1.2.1', name: '1.2.1 Project Coordinator BFDW 100%', group: '1.2', orgs: ['BfdW'] },
    { id: 26, code: '1.3.1.1', name: '1.3.1.1 Abroad (Europe - Vietnam) - BFDW', group: '1.3', orgs: ['BfdW'] },
    { id: 27, code: '1.3.1.2', name: '1.3.1.2 Abroad (Europe - Cambodia) - BFDW', group: '1.3', orgs: ['BfdW'] },
    { id: 28, code: '1.3.1.3', name: '1.3.1.3 Abroad (Europe - Indonesia) - BFDW', group: '1.3', orgs: ['BfdW'] },
    { id: 29, code: '1.3.1.4', name: '1.3.1.4 Abroad (Europe - Philippines) - BFDW', group: '1.3', orgs: ['BfdW'] },
    { id: 30, code: '1.3.1.5', name: '1.3.1.5 Abroad (Vietnam/Philippines - Cambodia) - BFDW & ICSC', group: '1.3', orgs: ['BfdW', 'ICSC'] },
    { id: 31, code: '1.3.1.6', name: '1.3.1.6 Abroad (Vietnam/Philippines - Indonesia) - BFDW & ICSC', group: '1.3', orgs: ['BfdW', 'ICSC'] },
    { id: 32, code: '1.3.1.7', name: '1.3.1.7 Abroad (Vietnam - Philippines) - BFDW & ICSC', group: '1.3', orgs: ['BfdW', 'ICSC'] },
    { id: 33, code: '1.3.2.1', name: '1.3.2.1 National (Across cities/provinces in a country) - AFV', group: '1.3', orgs: ['BfdW'] },
    { id: 34, code: '1.3.2.2', name: '1.3.2.2 National (Across cities/provinces in a country) - BFDW', group: '1.3', orgs: ['BfdW'] },
    { id: 35, code: '1.3.2.3', name: '1.3.2.3 Local (for stake holders travel within a city/province)', group: '1.3', orgs: ['BfdW', 'ICSC'] },
    { id: 36, code: '1.3.3.1', name: '1.3.3.1 Perdiem for 1 exposure visit from ASEAN Member States to European Union Member States', group: '1.3', orgs: ['ALL'] },
    { id: 37, code: '1.3.3.2', name: '1.3.3.2 Perdiem for activities within Vietnam - Cambodia - Indonesia - the Philippines', group: '1.3', orgs: ['ALL'] },
    { id: 38, code: '2.1.1', name: '2.1.1 Abroad (Europe - ASEAN) for kick-off workshop and closing workshop', group: '2.1', orgs: ['BfdW'] },
    { id: 39, code: '2.1.2', name: '2.1.2 Abroad (Vietnam - Cambodia - Indonesia - the Philipines) for monitoring trips', group: '2.1', orgs: ['BfdW'] },
    { id: 40, code: '2.1.3', name: '2.1.3 Abroad (Europe - ASEAN) for 1 exposure visit from ASEAN Member States to European Union Member States', group: '2.1', orgs: ['ALL'] },
    { id: 41, code: '2.1.4', name: '2.1.4 Abroad (Vietnam - Cambodia - Indonesia - the Philippines) For Activities', group: '2.1', orgs: ['ALL'] },
    { id: 42, code: '2.2.1', name: '2.2.1 National travel (Vietnam)', group: '2.2', orgs: ['BfdW'] },
    { id: 43, code: '2.2.2', name: '2.2.2 Local transportation for local stakeholders', group: '2.2', orgs: ['ALL'] },
    { id: 44, code: '2.2.3', name: '2.2.3 Travel for Seminar/conference participants', group: '2.2', orgs: ['ALL'] },
    { id: 46, code: '3.2.1', name: '3.2.1 Working station', group: '3', orgs: ['BfdW'] },
    { id: 47, code: '3.2.2', name: '3.2.2 Laptops', group: '3', orgs: ['BfdW', 'ICSC'] },
    { id: 48, code: '3.2.3', name: '3.2.3 Printers', group: '3', orgs: ['BfdW', 'ICSC'] },
    { id: 49, code: '3.2.4', name: '3.2.4 Computer Screen', group: '3', orgs: ['BfdW'] },
    { id: 52, code: '4.1.1', name: '4.1.1 Vietnam AFV (20%)', group: '4', orgs: ['BfdW'] },
    { id: 53, code: '4.1.2', name: '4.1.2 Philippines ICSC (16,4%)', group: '4', orgs: ['BfdW', 'ICSC'] },
    { id: 54, code: '4.2', name: '4.2 Consumables - office supplies', group: '4', orgs: ['BfdW', 'ICSC'] },
    { id: 55, code: '4.3.1', name: '4.3.1 Office other services (20%)', group: '4', orgs: ['BfdW'] },
    { id: 56, code: '4.3.2', name: '4.3.2 Office maintenance (20%)', group: '4', orgs: ['BfdW'] },
    { id: 57, code: '5.2.1', name: '5.2.1 Baseline Survey', group: '5.2', orgs: ['BfdW'] },
    { id: 58, code: '5.2.2', name: '5.2.2 Market Research', group: '5.2', orgs: ['BfdW'] },
    { id: 58, code: '5.3', name: '5.3 Expenditure verification', group: '5.3', orgs: ['BfdW'] },
    { id: 59, code: '5.4.1.1', name: '5.4.1.1 Mid-term Evaluation', group: '5.4', orgs: ['BfdW'] },
    { id: 60, code: '5.4.1.2', name: '5.4.1.2 Final Evaluation', group: '5.4', orgs: ['BfdW'] },
    { id: 61, code: '5.4.2.1', name: '5.4.2.1 Develop MnE system', group: '5.4', orgs: ['BfdW'] },
    { id: 58, code: '5.6', name: '5.6 Financial services (bank guarantee costs etc.)', group: '5.6', orgs: ['BfdW'] },
    { id: 62, code: '5.7.1', name: '5.7.1 Catering of conferences/seminars', group: '5.7', orgs: ['ALL'] },
    { id: 63, code: '5.7.2', name: '5.7.2 Venue rental of conferences/seminars', group: '5.7', orgs: ['ALL'] },
    { id: 64, code: '5.7.3', name: '5.7.3 Facilitation cost', group: '5.7', orgs: ['ALL'] },
    { id: 65, code: '5.7.4', name: '5.7.4 Group meeting cost', group: '5.7', orgs: ['ALL'] },
    { id: 66, code: '5.8.1', name: '5.8.1 Digital newsletters', group: '5.8', orgs: ['ALL'] },
    { id: 67, code: '5.8.2', name: '5.8.2 Digital banners/posters', group: '5.8', orgs: ['ALL'] },
    { id: 68, code: '5.8.3', name: '5.8.3 Advert for project activities', group: '5.8', orgs: ['ALL'] },
    { id: 69, code: '5.8.4', name: '5.8.4 Digital radio/television programs/social media campaigns', group: '5.8', orgs: ['ALL'] },
    { id: 70, code: '5.8.5', name: '5.8.5 Videos', group: '5.8', orgs: ['ALL'] },
    { id: 71, code: '5.8.6', name: '5.8.6 Communication and visibility items', group: '5.8', orgs: ['ALL'] },
    { id: 72, code: '6.1', name: '6.1 Develop and adapt multi-linguistic curriculum modules and learning resources for the EEP', group: '6', orgs: ['ALL'] },
    { id: 73, code: '6.2', name: '6.2 Develop and adapt 3 multi-linguistic platforms for the EEP', group: '6', orgs: ['ALL'] },
    { id: 74, code: '6.3', name: '6.3 Test and launch e-learning platforms for the EEP', group: '6', orgs: ['BfdW', 'ICSC'] },
    { id: 75, code: '6.4', name: '6.4 Support 50 youth-led initiatives', group: '6', orgs: ['ALL'] },
    { id: 76, code: '6.5', name: '6.5 Capacity Development in M&E and INTPA rules', group: '6', orgs: ['BfdW'] },
    { id: 77, code: '6.6', name: '6.6 Maintenance / digital manager', group: '6', orgs: ['BfdW', 'ICSC'] },
    { id: 78, code: '6.7', name: '6.7 Development EEP mobile app', group: '6', orgs: ['BfdW', 'ICSC'] },
  ];

  const fetchData =
      async () => {
        try {
          const result = await getOrganisations();
          const orgData = result.data;

          if (!cancelRequest.current) {
            if (orgData && Object.keys(orgData).length > 0) {
              setOrganisation(orgData[0]);
              setAvailableOrganisations(orgData);
            }
          }
        } catch (error) {
        }
      };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClickOpen = async () => {
    setOpen(true);

    let selectedCurrency = currencyOptions.find(c => c.code === 'USD');
    let selectedIntermediateCurrency = currencyOptions.find(c => c.code === 'USD');
    let budgetGroup = budgetGroupOptions.find(bg => bg.code === '2.2');
    let budgetLine = budgetOptions.find(b => b.code === '2.2.2');

    if (entity) {
      selectedCurrency = currencyOptions.find(c => c.code === entity.currency);
      selectedIntermediateCurrency = currencyOptions.find(c => c.code === entity.intermediateCurrency);

      budgetLine = budgetOptions.find(bl => bl.name === entity.budgetLine);
      budgetGroup = budgetGroupOptions.find(bg => bg.code === budgetLine.group);

      await receiptForm.setFieldValue('payee', entity.payee);
      await receiptForm.setFieldValue('date', entity.transactionDate);
      await receiptForm.setFieldValue('description', entity.description);
      await receiptForm.setFieldValue('invoiceNumber', entity.invoiceNumber);
      await receiptForm.setFieldValue('amount', entity.amount);
      await receiptForm.setFieldValue('exchangeRate', entity.exchangeRate);
      await receiptForm.setFieldValue('intermediateExchangeRate', entity.intermediateExchangeRate);
      await receiptForm.setFieldValue('transactionRefPartner', entity.transactionRefPartner);
      await receiptForm.setFieldValue('outputActivity', entity.outputActivity);
      await receiptForm.setFieldValue('currency', selectedCurrency);
      await receiptForm.setFieldValue('intermediateCurrency', selectedIntermediateCurrency);
      await receiptForm.setFieldValue('budgetGroup', budgetGroup.code);
      await receiptForm.setFieldValue('budget', budgetLine.code);

      if (entity.intermediateExchangeRate > 0) {
        setHideIntermediateFields(false);
      }
    }

    setDescription(entity && entity.description ? entity.description : '');
    setInvoiceNumber(entity && entity.invoiceNumber ? entity.invoiceNumber : '');
    setAmount(entity && entity.amount ? entity.amount : '');
    setExchangeRate(entity && entity.exchangeRate ? entity.exchangeRate : '');
    setIntermediateExchangeRate(entity && entity.intermediateExchangeRate ? entity.intermediateExchangeRate : '');
    setTransactionRefPartner(entity && entity.transactionRefPartner ? entity.transactionRefPartner : '');
    setDate(entity && entity.transactionDate ? entity.transactionDate : '');
    setPayee(entity && entity.payee ? entity.payee : '');
    setOutputActivity(entity && entity.outputActivity ? entity.outputActivity : undefined);
    setCurrency(entity && entity.currency ? selectedCurrency : { id: 2, code: 'USD', name: 'US Dollar' });
    setIntermediateCurrency(
        entity && entity.intermediateCurrency ? selectedIntermediateCurrency : { id: 2, code: 'USD', name: 'US Dollar' });
    setAttachments(entity && entity.attachments ? entity.attachments : []);
    setBudgetGroup(entity && entity.budgetLine ? budgetGroup.code : '');
    setBudget(entity && entity.budgetLine ? budgetLine.code : '');
    setOrganisation(entity && entity.organisation ? entity.organisation.id : authState.org ? authState.org.split('_')[1] + ''  : undefined);
  };

  let filterByOrg = 'ALL';
  if (authState.org && authState.org.split('_')[1] === '3') {
    filterByOrg = 'BfdW';
  }
  if (authState.org && authState.org.split('_')[1] === '6') {
    filterByOrg = 'ICSC';
  }

  const handleDeleteAttachment = (fileToDelete) => {
    setAttachments(attachments.filter(file => file !== fileToDelete));
  };

  const handleClose = async (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return;
    }
    setOpen(false);
    setDescription('');
    setInvoiceNumber('');
    setAmount('');
    setExchangeRate('');
    setIntermediateExchangeRate('');
    setTransactionRefPartner('');
    setDate('');
    setPayee('');
    setOutputActivity(undefined);
    setCurrency({ id: 2, code: 'USD', name: 'US Dollar' });
    setIntermediateCurrency({ id: 2, code: 'USD', name: 'US Dollar' });
    setAttachments([]);
    setBudgetGroup('');
    setBudget('');
    setOrganisation(undefined);
    setHideIntermediateFields(true);
    clearFormData();
    await receiptForm.resetForm();
  };

  const handleConfirm = () => {
    let selectedBudgetName = budgetOptions.find(b => b.code === budget);
    const budgetName = selectedBudgetName ? selectedBudgetName.name : '';

    const selectedOrganisation = availableOrganisations.find(p => p.id + '' === organisation + '');

    action === 'create' ?
        confirmationHandler(
            selectedOrganisation,
            invoiceNumber,
            budgetName,
            date,
            transactionRefPartner,
            payee,
            description,
            amount,
            currency.code,
            intermediateCurrency.code,
            exchangeRate,
            intermediateExchangeRate,
            outputActivity,
            attachments) :
        confirmationHandler(entity.id,
            selectedOrganisation,
            invoiceNumber,
            budgetName,
            date,
            transactionRefPartner,
            payee,
            description,
            amount,
            currency.code,
            intermediateCurrency.code,
            exchangeRate,
            intermediateExchangeRate,
            outputActivity,
            attachments);
    handleClose();
  };

  const clearFormData = () => {
    setDescription('');
  };

  const handleOrganisationChange = (event) => {
    setOrganisation(availableOrganisations.find(p => p.id === event.target.value).id);
  };

  const handleBudgetGroupChange = (event) => {
    setBudgetGroup(event.target.value);
    receiptForm.setFieldValue('budgetGroup', event.target.value);
  };

  const handleBudgetChange = (event) => {
    setBudget(event.target.value);
    receiptForm.setFieldValue('budget', event.target.value);
  };

  const filteredBudgetOptions = budgetOptions.filter(option => option.group === budgetGroup);

  const onTargetClick = () => {
    fileInputRef.current.click()
  }

  const onFileInputChange = (event) => {
    const { files } = event.target;
    setAttachments([
      ...attachments,
      ...files
    ]);
  }

  function calculateConvertedAmount(amountToConvert, rate) {
    return Math.round(((amountToConvert / rate) + Number.EPSILON) * 100) / 100;
  }

  // Converts through both the intermediate and reporting exchange rate in one step and
  // rounds once, so the result matches the backend's per-receipt conversion exactly
  // (rounding after each intermediate step compounds cent-level errors).
  function calculateReportingAmount(amountToConvert, intermediateRate, rate) {
    const rawAmount = intermediateRate > 0
      ? amountToConvert / intermediateRate / rate
      : amountToConvert / rate;
    return Math.round((rawAmount + Number.EPSILON) * 100) / 100;
  }

  return (
      <div>
        {action === 'create' &&
          <Fab
              variant="extended"
              size="medium"
              color="secondary"
              aria-label="add"
              onClick={handleClickOpen}
              className={classes.fab}
              style={{position: 'fixed'}}
          >
            <AddIcon sx={{ fontSize: 24 }}/>
            Add receipt
          </Fab>
        }
        { action === 'edit' &&
            <Tooltip title="Edit">
              <IconButton aria-label="edit"
                          onClick={handleClickOpen}>
                <EditIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
        }
        <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">{ action === 'create' ? 'Add new' : 'Edit'} receipt</DialogTitle>
          <DialogContent>
            <DescriptionList>
              <DescriptionTerm>Organisation</DescriptionTerm>
              <Description>
                <Select
                    labelId="org-select"
                    id="org-select"
                    value={organisation}
                    variant="outlined"
                    onChange={handleOrganisationChange}
                    disabled={!authState.org || (authState.org && authState.org.split('_')[1] !== '3')}
                >
                  {availableOrganisations.length > 0 && availableOrganisations.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </Description>
            </DescriptionList>
            <InvoiceNumberAutocomplete
                label="Invoice number"
                handleChange={(value) => {
                  receiptForm.setFieldValue('invoiceNumber', value);
                  setInvoiceNumber(value);
                }}
                searchFct={searchInvoiceNumbers}
                currentValue={invoiceNumber}
                error={Boolean(receiptForm.errors.invoiceNumber)}
                helperText={receiptForm.errors.invoiceNumber}

            />
            <DescriptionList>
              <DescriptionTerm>Budget Group</DescriptionTerm>
              <Description>
                <Select
                    labelId="budget-group-select"
                    id="budget-group-select"
                    value={budgetGroup}
                    variant="outlined"
                    onChange={handleBudgetGroupChange}
                    error={Boolean(receiptForm.errors.budgetGroup)}
                    helperText={receiptForm.errors.budgetGroup}
                >
                  {budgetGroupOptions.length > 0 && budgetGroupOptions.filter(bgo => {
                    if (filterByOrg === 'ALL') {
                      return !bgo.orgs || bgo.orgs.includes('ALL');
                    } else if (filterByOrg === 'BfdW') {
                      return !bgo.orgs || bgo.orgs.includes('ALL') || bgo.orgs.includes('BfdW');
                    } else if (filterByOrg === 'ICSC') {
                      return !bgo.orgs || bgo.orgs.includes('ALL') || bgo.orgs.includes('ICSC');
                    }
                  }).map((p) => (
                      <MenuItem key={p.id} value={p.code}>{p.name}</MenuItem>
                  ))}
                </Select>
              </Description>
            </DescriptionList>
            { budgetGroup !== '' &&
                <DescriptionList>
                  <DescriptionTerm>Budget</DescriptionTerm>
                  <Description>
                    <Select
                        labelId="budget-select"
                        id="budget-select"
                        value={budget}
                        variant="outlined"
                        onChange={handleBudgetChange}
                        error={Boolean(receiptForm.errors.budget)}
                        helperText={receiptForm.errors.budget}
                    >
                      {filteredBudgetOptions.length > 0 && filteredBudgetOptions.filter(bgo => {
                        if (filterByOrg === 'ALL') {
                          return !bgo.orgs || bgo.orgs.includes('ALL');
                        } else if (filterByOrg === 'BfdW') {
                          return !bgo.orgs || bgo.orgs.includes('ALL') || bgo.orgs.includes('BfdW');
                        } else if (filterByOrg === 'ICSC') {
                          return !bgo.orgs || bgo.orgs.includes('ALL') || bgo.orgs.includes('ICSC');
                        }
                      }).map((p) => (
                          <MenuItem key={p.id} value={p.code}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </Description>
                </DescriptionList>
            }
            <TextField
                margin="normal"
                id="date"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  receiptForm.setFieldValue('date', e.target.value);
                }}
                fullWidth
                variant="outlined"
                error={Boolean(receiptForm.errors.date)}
                helperText={receiptForm.errors.date}
            />
            <TextField
                margin="normal"
                id="transactionRefPartner"
                label="Transaction reference"
                type="text"
                value={transactionRefPartner}
                onChange={(e) => {
                  setTransactionRefPartner(e.target.value);
                  receiptForm.setFieldValue('transactionRefPartner', e.target.value);
                }}
                fullWidth
                variant="outlined"
                error={Boolean(receiptForm.errors.transactionRefPartner)}
                helperText={receiptForm.errors.transactionRefPartner}
            />
            <TextField
                margin="normal"
                id="payee"
                label="Payee"
                type="text"
                value={payee}
                onChange={(e) => {
                  setPayee(e.target.value);
                  receiptForm.setFieldValue('payee', e.target.value);
                }}
                fullWidth
                variant="outlined"
                error={Boolean(receiptForm.errors.payee)}
                helperText={receiptForm.errors.payee}
            />
            <TextField
                margin="normal"
                id="description"
                label="Description"
                type="text"
                multiline
                rows={2}
                value={description}
                onChange={(e) => {
                    setDescription(e.target.value);
                    receiptForm.setFieldValue('description', e.target.value);
                }}
                fullWidth
                variant="outlined"
                error={Boolean(receiptForm.errors.description)}
                helperText={receiptForm.errors.description}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <FormControl sx={{ m: 1, marginLeft: 0 }}>
                <InputLabel htmlFor="amount">Amount</InputLabel>
                <OutlinedInput
                    id="amount"
                    label="Amount"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      receiptForm.setFieldValue('amount', e.target.value);
                    }}
                    sx={{ width: 200 }}
                    type="number"
                    error={Boolean(receiptForm.errors.amount)}
                    helperText={receiptForm.errors.amount}
                />
              </FormControl>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={hideIntermediateFields} onChange={(event) => {
                  setHideIntermediateFields(event.target.checked ? 1 : 0);
                  if (event.target.checked) {
                    setIntermediateCurrency({ id: 2, code: 'USD', name: 'US Dollar' });
                    setIntermediateExchangeRate('');
                  }
                }} />} label="Invoice currency = functional currency" />
              </FormGroup>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              { !hideIntermediateFields ? (
                  <Autocomplete
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={intermediateCurrency}
                      onChange={(e, selectedValue) => {
                        setIntermediateCurrency(selectedValue);
                      }}
                      options={currencyOptions}
                      getOptionLabel={(option) => (option.code + ' - ' + option.name)}
                      sx={{ width: 300, mr: 1 }}
                      renderInput={(params) => <TextField {...params} label={`${hideIntermediateFields ? 'Invoice' : 'Functional'} currency`} />}
                  />
              ) : ''}
              <Autocomplete
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={currency}
                  onChange={(e, selectedValue) => {
                    setCurrency(selectedValue);
                    if (selectedValue && selectedValue.code === 'EUR') {
                      setExchangeRate(1);
                      receiptForm.setFieldValue('exchangeRate', 1);
                    }
                  }}
                  options={currencyOptions}
                  getOptionLabel={(option) => (option.code + ' - ' + option.name)}
                  sx={{ width: 300}}
                  renderInput={(params) => <TextField {...params} label="Invoice currency" />}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              { !hideIntermediateFields ? (
                <FormControl sx={{ m: 1, marginLeft: 0 }}>
                  <InputLabel htmlFor="amount">Intermediate exchange rate</InputLabel>
                  <OutlinedInput
                      disabled={currency && currency.code === 'EUR'}
                      id="intermediateExchangeRate"
                      label="Intermediate exchange rate"
                      value={intermediateExchangeRate}
                      onChange={(e) => {
                        setIntermediateExchangeRate(e.target.value);
                        receiptForm.setFieldValue('intermediateExchangeRate', e.target.value);
                      }}
                      sx={{ width: 300 }}
                      type="number"
                      error={Boolean(receiptForm.errors.intermediateExchangeRate)}
                      helperText={receiptForm.errors.intermediateExchangeRate}
                  />
                </FormControl>
              ) : ''}
              <FormControl sx={{ m: 1, marginLeft: 0 }}>
                <InputLabel htmlFor="amount">Exchange rate to EUR</InputLabel>
                <OutlinedInput
                    disabled={currency && currency.code === 'EUR'}
                    id="exchangeRate"
                    label="Exchange rate to EUR"
                    value={exchangeRate}
                    onChange={(e) => {
                      setExchangeRate(e.target.value);
                      receiptForm.setFieldValue('exchangeRate', e.target.value);
                    }}
                    sx={{ width: 300 }}
                    type="number"
                    error={Boolean(receiptForm.errors.exchangeRate)}
                    helperText={receiptForm.errors.exchangeRate}
                />
              </FormControl>
            </Box>
            { !hideIntermediateFields &&
                <Typography variant="subtitle1">Functional amount: {amount > 0 && intermediateExchangeRate > 0 ? `${calculateConvertedAmount(amount, intermediateExchangeRate)} ${intermediateCurrency ? intermediateCurrency.code : ''}` : ''} </Typography>
            }
            <Typography variant="subtitle1">Reporting amount: {amount > 0 && exchangeRate > 0 ? `${calculateReportingAmount(amount, intermediateExchangeRate, exchangeRate)} €` : ''} </Typography>
            <BasicAutocomplete
                label="Output Activity"
                handleChange={(value) => {
                  setOutputActivity(value);
                  receiptForm.setFieldValue('outputActivity', value);
                }}
                searchFct={searchOutputActivities}
                currentValue={outputActivity}
                property={'name'}
                scndProperty={'description'}
                error={Boolean(receiptForm.errors.outputActivity)}
                helperText={receiptForm.errors.outputActivity}
            />
            <br/>
            <input
                onChange={onFileInputChange}
                ref={fileInputRef}
                type="file"
                multiple="multiple"
                style={{display:'none'}}
            />
            { attachments && attachments.length > 0 && attachments.map(file => (
                <Chip
                    sx={{margin: 1}}
                    avatar={
                      <Avatar>
                        {file.name ? file.name.slice(file.name.lastIndexOf('.') + 1) : file.filename.slice(file.filename.lastIndexOf('.') + 1)}
                      </Avatar>}
                    label={file.name ? file.name : file.filename}
                    onDelete={() => handleDeleteAttachment(file)}
                    deleteIcon={<CloseIcon />}
                />
            ))}
            <div style={{ backgroundColor: '#EEEEEE', borderRadius: '5px', border: '1px solid #EEEEEE', width: 850, color: '#666666', padding: 20 }}>
              <FileDrop
                  onDrop={(files) => {
                    setAttachments([...attachments, ...files]);
                  }}
                  onTargetClick={onTargetClick}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  margin: '16px 16px',
                }}>
                  <AttachmentIcon sx={{ fontSize: 24, marginRight: 1 }} />
                  <span>Drop one or multiple documents here for documentation!</span>
                </div>

              </FileDrop>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button disabled={!receiptForm.dirty || Object.keys(receiptForm.errors).length !== 0} onClick={handleConfirm} color="primary">
              { action === 'create' ? 'Add' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
}